import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import WidgetHeader from "./WidgetHeader";
import {
  STAT_CATEGORIES,
  getStatOptionsByCategory,
  getStatLabel,
  getPlayerDataFieldName,
} from "../../constants/stats";
import { useGetPlayerByIdQuery } from "../../store/api/mlbApi";
import { buildEchartsOptions } from "../../utils/chartTheme";

const PlayerChartWidget = ({ config, onEdit, onDelete }) => {
  const chartRef = useRef(null);

  // Get stat category from config, default to hitting for backward compatibility
  const statCategory =
    config.config?.statCategory ||
    config.statCategory ||
    STAT_CATEGORIES.HITTING;

  const statOptions = useMemo(
    () => getStatOptionsByCategory(statCategory),
    [statCategory]
  );

  // Memoize initial values to prevent infinite re-renders
  const initialSelectedStat = useMemo(() => {
    const savedStat = config.config?.selectedStat || config.selectedStat;
    // Check if the saved stat is valid for the current category
    const isValidStat = statOptions.some(
      (option) => option.value === savedStat
    );
    if (isValidStat) {
      return savedStat;
    }
    // If not valid, use the first stat in the current category
    return statOptions.length > 0 ? statOptions[0].value : "homeruns";
  }, [config.config?.selectedStat, config.selectedStat, statOptions]);

  const initialNormalizeByCareer = useMemo(
    () =>
      config.config?.normalizeByCareer !== undefined
        ? config.config.normalizeByCareer
        : config.normalizeByCareer !== undefined
        ? config.normalizeByCareer
        : true,
    [config.config?.normalizeByCareer, config.normalizeByCareer]
  );

  const [selectedStat, setSelectedStat] = useState(initialSelectedStat);
  const [normalizeByCareer, setNormalizeByCareer] = useState(
    initialNormalizeByCareer
  );

  // Fetch player data dynamically - call hooks at top level
  const playerIds = config.config?.players?.map((p) => p.id) || [];

  // Call hooks individually for up to 5 players (max comparison size)
  const playerQuery1 = useGetPlayerByIdQuery(playerIds[0] || null, {
    skip: !playerIds[0],
  });
  const playerQuery2 = useGetPlayerByIdQuery(playerIds[1] || null, {
    skip: !playerIds[1],
  });
  const playerQuery3 = useGetPlayerByIdQuery(playerIds[2] || null, {
    skip: !playerIds[2],
  });
  const playerQuery4 = useGetPlayerByIdQuery(playerIds[3] || null, {
    skip: !playerIds[3],
  });
  const playerQuery5 = useGetPlayerByIdQuery(playerIds[4] || null, {
    skip: !playerIds[4],
  });

  const playerQueries = [
    playerQuery1,
    playerQuery2,
    playerQuery3,
    playerQuery4,
    playerQuery5,
  ].slice(0, playerIds.length);

  // Check if any queries are still loading
  const isLoading = playerQueries.some((query) => query.isLoading);

  // Process player data when loaded
  const processedPlayers = useMemo(() => {
    if (playerQueries.every((query) => query.data && !query.isLoading)) {
      return playerQueries
        .map((query, index) => {
          const player = query.data;
          const playerConfig = config.config.players[index];

          if (!player?.stats?.[statCategory]?.seasons) {
            return null;
          }

          // Extract the selected stat data
          const seasons = player.stats[statCategory].seasons;
          const years = Object.keys(seasons).sort();

          const statData = years.map((year, yearIndex) => {
            const seasonData = seasons[year];
            // Convert leaderboard stat name to player data field name
            const playerDataFieldName = getPlayerDataFieldName(selectedStat);
            const statValue =
              parseFloat(seasonData[playerDataFieldName] || 0) || 0;

            if (normalizeByCareer) {
              return {
                year: (yearIndex + 1).toString(),
                value: statValue,
                actualYear: year,
              };
            } else {
              return {
                year,
                value: statValue,
              };
            }
          });

          return {
            id: playerConfig.id,
            name: playerConfig.name,
            chartData: {
              [selectedStat]: statData,
            },
          };
        })
        .filter(Boolean);
    }
    return [];
  }, [
    playerQueries,
    statCategory,
    selectedStat,
    normalizeByCareer,
    config.config.players,
  ]);

  // Update selected stat when stat category changes (for backward compatibility)
  useEffect(() => {
    const savedStat = config.config?.selectedStat || config.selectedStat;
    const isValidStat = statOptions.some(
      (option) => option.value === savedStat
    );
    if (!isValidStat && statOptions.length > 0) {
      setSelectedStat(statOptions[0].value);
    }
  }, [
    statCategory,
    statOptions,
    config.config?.selectedStat,
    config.selectedStat,
  ]);

  // Handle chart resize on mount and when dependencies change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [selectedStat, normalizeByCareer, processedPlayers]);

  // Additional resize effect for when the widget is first created
  useEffect(() => {
    const timer = setTimeout(() => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const renderChart = useCallback(() => {
    if (!processedPlayers || processedPlayers.length === 0) {
      return {
        tooltip: { show: false },
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
      };
    }

    // Use the processed players with their chart data
    const validPlayers = processedPlayers.filter(
      (player) => player && player.chartData && player.chartData[selectedStat]
    );

    if (validPlayers.length === 0) {
      return {
        tooltip: { show: false },
        xAxis: { show: false },
        yAxis: { show: false },
        series: [],
      };
    }

    // Get all available years across all players for the selected stat
    let allYears = [];
    if (validPlayers && validPlayers.length > 0) {
      if (normalizeByCareer) {
        // For career-normalized view, use career years (1, 2, 3, etc.)
        const maxCareerYears = Math.max(
          ...validPlayers.map((player) => {
            const statData = player.chartData[selectedStat] || [];
            return statData.length;
          })
        );
        allYears = Array.from({ length: maxCareerYears }, (_, i) =>
          (i + 1).toString()
        );
      } else {
        // For calendar view, use actual years
        allYears = validPlayers
          .reduce((years, player) => {
            const statData = player.chartData[selectedStat] || [];
            const playerYears = statData.map((d) => d.year);
            return [...new Set([...years, ...playerYears])];
          }, [])
          .sort();
      }
    }

    // Create series data for each player - ensure clean data structure
    const series = validPlayers.map((player, index) => {
      const statData = player.chartData[selectedStat] || [];

      let data;
      if (normalizeByCareer) {
        // For career-normalized view, map by career year order
        data = allYears.map((careerYear, yearIndex) => {
          const dataPoint = statData[yearIndex];
          return dataPoint ? Number(dataPoint.value) : null;
        });
      } else {
        // For calendar view, map by actual years
        data = allYears.map((year) => {
          const dataPoint = statData.find((d) => d.year === year);
          return dataPoint ? Number(dataPoint.value) : null;
        });
      }

      // Create a completely clean series object
      return {
        name: String(player.name),
        type: "line",
        data: data,
        connectNulls: true,
        symbol: "circle",
        symbolSize: 8,
        emphasis: {
          focus: "series",
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      };
    });

    // Create a completely clean chart options object
    return buildEchartsOptions({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      legend: {
        data: (validPlayers || []).map((player) => String(player.name)),
        top: 0,
      },
      grid: {
        left: "5%",
        right: "4%",
        bottom: "12%",
        containLabel: true,
        top: 80,
      },
      xAxis: {
        type: "category",
        data: (allYears || []).map((year) => String(year)),
        name: normalizeByCareer ? "Career Year" : "Year",
        nameLocation: "middle",
        nameGap: 45,
        axisLabel: {
          show: true,
          margin: 15,
        },
      },
      yAxis: {
        type: "value",
        name: String(getStatLabel(selectedStat)),
        nameLocation: "middle",
        nameGap: 55,
        axisLabel: {
          show: true,
          margin: 15,
        },
      },
      series: series || [],
    });
  }, [processedPlayers, selectedStat, normalizeByCareer]);

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <WidgetHeader
        title={config.title}
        onEdit={onEdit ? () => onEdit(config) : null}
        onDelete={onDelete ? () => onDelete(config) : null}
        showDragHandle={Boolean(onEdit || onDelete)}
      />

      {/* Chart Controls */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedStat}
            onChange={(e) => setSelectedStat(e.target.value)}
            displayEmpty
          >
            {statOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={normalizeByCareer ? "Career Years" : "Overlay"}
          onClick={() => setNormalizeByCareer(!normalizeByCareer)}
          clickable
          variant={normalizeByCareer ? "filled" : "outlined"}
          color={normalizeByCareer ? "primary" : "default"}
          size="small"
        />
      </Box>

      {/* Chart Content */}
      <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
        <Paper sx={{ p: 2, height: "100%" }} elevation={0}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ReactECharts
              ref={chartRef}
              option={renderChart()}
              style={{ height: "100%" }}
              notMerge={true}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PlayerChartWidget;
